import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { LogOut, Plus, Trash2, Edit, FileText, Settings, ArrowLeft, FolderOpen, RefreshCw, Archive, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category Card Component - moved outside main component
const CategoryCard = ({ 
  category, 
  isExpanded, 
  onToggleExpand, 
  onEdit, 
  onArchive, 
  onRestore, 
  onDeletePermanently 
}) => {
  const isArchived = category.is_archived;

  return (
    <div className={`border rounded-lg ${isArchived ? 'bg-slate-50 border-slate-300' : 'bg-white border-slate-200'}`}>
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
        onClick={() => onToggleExpand(category.category_id)}
      >
        <div className="flex items-center gap-3">
          <FolderOpen className={`h-5 w-5 ${isArchived ? 'text-slate-400' : 'text-blue-600'}`} />
          <div>
            <div className="flex items-center gap-2">
              <h3 className={`font-medium ${isArchived ? 'text-slate-500' : 'text-slate-900'}`}>
                {category.name}
              </h3>
              {isArchived && (
                <span className="px-2 py-0.5 text-xs bg-slate-200 text-slate-600 rounded">
                  Αρχειοθετημένο
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500">
              Key: {category.key} • {category.documents.length} δικαιολογητικά
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {!isArchived && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); onEdit(category); }}
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              >
                <Edit className="h-4 w-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => e.stopPropagation()}
                    className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                  >
                    <Archive className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Αρχειοθέτηση Κατηγορίας</AlertDialogTitle>
                    <AlertDialogDescription>
                      Η κατηγορία &ldquo;{category.name}&rdquo; θα αρχειοθετηθεί και δεν θα είναι διαθέσιμη για νέα τιμολόγια. 
                      Μπορείτε να την επαναφέρετε αργότερα.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => onArchive(category.category_id)}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      Αρχειοθέτηση
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
          
          {isArchived && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); onRestore(category.category_id); }}
                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => e.stopPropagation()}
                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Οριστική Διαγραφή</AlertDialogTitle>
                    <AlertDialogDescription>
                      Η κατηγορία &ldquo;{category.name}&rdquo; θα διαγραφεί οριστικά. 
                      Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => onDeletePermanently(category.category_id)}
                      className="bg-rose-600 hover:bg-rose-700"
                    >
                      Διαγραφή
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
          
          {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
        </div>
      </div>
      
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-slate-100">
          <div className="mt-3">
            <h4 className="text-sm font-medium text-slate-700 mb-2">Δικαιολογητικά:</h4>
            <ul className="space-y-1">
              {category.documents.map((doc, idx) => (
                <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                  <span className="text-slate-400 mt-0.5">{idx + 1}.</span>
                  <span>{doc}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

// Document Form Fields Component - moved outside main component
const DocumentFormFields = ({ documents, onAddDocument, onRemoveDocument, onUpdateDocument }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between">
      <Label>Δικαιολογητικά</Label>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onAddDocument}
      >
        <Plus className="h-4 w-4 mr-1" />
        Προσθήκη
      </Button>
    </div>
    
    <div className="space-y-2 max-h-60 overflow-y-auto">
      {documents.map((doc, index) => (
        <div key={index} className="flex items-center gap-2">
          <span className="text-sm text-slate-500 w-6">{index + 1}.</span>
          <Input
            value={doc}
            onChange={(e) => onUpdateDocument(index, e.target.value)}
            placeholder="Όνομα δικαιολογητικού"
            className="flex-1"
          />
          {documents.length > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onRemoveDocument(index)}
              className="text-rose-600 hover:text-rose-700"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ))}
    </div>
  </div>
);

const ExpenseCategoryAdmin = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Determine API prefix based on user role
  const apiPrefix = user?.is_admin ? '/admin/expense-categories' : '/expense-categories';
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    key: '',
    name: '',
    category_type: 'expense',
    documents: [''],
  });

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}${apiPrefix}?include_archived=${showArchived}`);
      setCategories(response.data);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης κατηγοριών');
    } finally {
      setLoading(false);
    }
  }, [showArchived, apiPrefix]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const toggleExpand = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const resetForm = () => {
    setFormData({
      key: '',
      name: '',
      category_type: 'expense',
      documents: [''],
    });
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!formData.key || !formData.name) {
      toast.error('Συμπληρώστε όλα τα υποχρεωτικά πεδία');
      return;
    }
    
    const filteredDocs = formData.documents.filter(d => d.trim() !== '');
    if (filteredDocs.length === 0) {
      toast.error('Προσθέστε τουλάχιστον ένα δικαιολογητικό');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}${apiPrefix}`, {
        key: formData.key,
        name: formData.name,
        category_type: formData.category_type,
        documents: filteredDocs,
      });
      toast.success('Η κατηγορία δημιουργήθηκε');
      setCreateDialogOpen(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα δημιουργίας κατηγορίας');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    if (!selectedCategory) return;

    const filteredDocs = formData.documents.filter(d => d.trim() !== '');
    if (filteredDocs.length === 0) {
      toast.error('Προσθέστε τουλάχιστον ένα δικαιολογητικό');
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}${apiPrefix}/${selectedCategory.category_id}`, {
        name: formData.name,
        documents: filteredDocs,
      });
      toast.success('Η κατηγορία ενημερώθηκε');
      setEditDialogOpen(false);
      setSelectedCategory(null);
      resetForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα ενημέρωσης κατηγορίας');
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveCategory = async (categoryId) => {
    try {
      await axios.delete(`${API}${apiPrefix}/${categoryId}`);
      toast.success('Η κατηγορία αρχειοθετήθηκε');
      fetchCategories();
    } catch (error) {
      toast.error('Σφάλμα αρχειοθέτησης');
    }
  };

  const handleRestoreCategory = async (categoryId) => {
    try {
      await axios.post(`${API}${apiPrefix}/${categoryId}/restore`);
      toast.success('Η κατηγορία επαναφέρθηκε');
      fetchCategories();
    } catch (error) {
      toast.error('Σφάλμα επαναφοράς');
    }
  };

  const handleDeletePermanently = async (categoryId) => {
    try {
      await axios.delete(`${API}${apiPrefix}/${categoryId}?hard_delete=true`);
      toast.success('Η κατηγορία διαγράφηκε οριστικά');
      fetchCategories();
    } catch (error) {
      toast.error('Σφάλμα διαγραφής');
    }
  };

  const openEditDialog = (category) => {
    setSelectedCategory(category);
    setFormData({
      key: category.key,
      name: category.name,
      category_type: category.category_type,
      documents: category.documents.length > 0 ? category.documents : [''],
    });
    setEditDialogOpen(true);
  };

  const addDocumentField = () => {
    setFormData(prev => ({
      ...prev,
      documents: [...prev.documents, '']
    }));
  };

  const removeDocumentField = (index) => {
    if (formData.documents.length > 1) {
      setFormData(prev => ({
        ...prev,
        documents: prev.documents.filter((_, i) => i !== index)
      }));
    }
  };

  const updateDocument = (index, value) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.map((doc, i) => i === index ? value : doc)
    }));
  };

  const expenseCategories = categories.filter(c => c.category_type === 'expense');
  const generalCategories = categories.filter(c => c.category_type === 'general');

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">Admin Panel</h2>
          <p className="text-sm text-slate-600 mt-1">{user.email}</p>
        </div>

        <nav className="p-4">
          <div className="space-y-2">
            <Button
              onClick={() => navigate('/admin')}
              variant="ghost"
              className="w-full justify-start"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Διαχείριση Χρηστών
            </Button>
            <Button
              onClick={() => navigate('/admin/templates')}
              variant="ghost"
              className="w-full justify-start"
            >
              <FileText className="mr-2 h-4 w-4" />
              Πρότυπα Αίτησης
            </Button>
            <Button
              onClick={() => navigate('/admin/implementation-templates')}
              variant="ghost"
              className="w-full justify-start"
            >
              <Settings className="mr-2 h-4 w-4" />
              Πρότυπα Υλοποίησης
            </Button>
            <div className="px-4 py-2 bg-slate-100 rounded-md text-slate-900 font-medium">
              Κατηγορίες Δαπανών
            </div>
          </div>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
          <Button
            onClick={onLogout}
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-slate-900"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Αποσύνδεση
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        <div className="p-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900">
                Κατηγορίες Δαπανών
              </h1>
              <p className="text-slate-600 mt-1">
                Διαχείριση κατηγοριών δαπανών και δικαιολογητικών υλοποίησης
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => setShowArchived(!showArchived)}
                variant="outline"
                className={showArchived ? 'border-amber-600 text-amber-600' : ''}
              >
                <Archive className="mr-2 h-4 w-4" />
                {showArchived ? 'Απόκρυψη Αρχειοθετημένων' : 'Εμφάνιση Αρχειοθετημένων'}
              </Button>
              
              <Button
                onClick={fetchCategories}
                variant="outline"
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>

              <Dialog open={createDialogOpen} onOpenChange={(open) => { setCreateDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button className="bg-slate-900 hover:bg-slate-800">
                    <Plus className="mr-2 h-4 w-4" />
                    Νέα Κατηγορία
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Νέα Κατηγορία Δαπάνης</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateCategory} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="key">Key (μοναδικό αναγνωριστικό)</Label>
                      <Input
                        id="key"
                        value={formData.key}
                        onChange={(e) => setFormData({ ...formData, key: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                        placeholder="π.χ. equipment"
                        required
                      />
                      <p className="text-xs text-slate-500">Χρησιμοποιήστε λατινικούς χαρακτήρες χωρίς κενά</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="name">Όνομα Κατηγορίας</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="π.χ. Εξοπλισμός / Μηχανήματα"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Τύπος</Label>
                      <Select
                        value={formData.category_type}
                        onValueChange={(value) => setFormData({ ...formData, category_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="expense">Κατηγορία Δαπάνης (για τιμολόγια)</SelectItem>
                          <SelectItem value="general">Γενικά Δικαιολογητικά (ανά έργο)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <DocumentFormFields 
                      documents={formData.documents}
                      onAddDocument={addDocumentField}
                      onRemoveDocument={removeDocumentField}
                      onUpdateDocument={updateDocument}
                    />

                    <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={loading}>
                      {loading ? 'Δημιουργία...' : 'Δημιουργία Κατηγορίας'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Expense Categories Section */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-blue-600" />
              Κατηγορίες Δαπανών (Τιμολόγια)
            </h2>
            <div className="space-y-3">
              {expenseCategories.length === 0 ? (
                <div className="p-8 text-center text-slate-500 bg-white border border-slate-200 rounded-lg">
                  Δεν υπάρχουν κατηγορίες δαπανών
                </div>
              ) : (
                expenseCategories.map(category => (
                  <CategoryCard 
                    key={category.category_id} 
                    category={category}
                    isExpanded={expandedCategories[category.category_id]}
                    onToggleExpand={toggleExpand}
                    onEdit={openEditDialog}
                    onArchive={handleArchiveCategory}
                    onRestore={handleRestoreCategory}
                    onDeletePermanently={handleDeletePermanently}
                  />
                ))
              )}
            </div>
          </div>

          {/* General Categories Section */}
          <div>
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-600" />
              Γενικά Δικαιολογητικά (ανά έργο)
            </h2>
            <div className="space-y-3">
              {generalCategories.length === 0 ? (
                <div className="p-8 text-center text-slate-500 bg-white border border-slate-200 rounded-lg">
                  Δεν υπάρχουν γενικές κατηγορίες
                </div>
              ) : (
                generalCategories.map(category => (
                  <CategoryCard 
                    key={category.category_id} 
                    category={category}
                    isExpanded={expandedCategories[category.category_id]}
                    onToggleExpand={toggleExpand}
                    onEdit={openEditDialog}
                    onArchive={handleArchiveCategory}
                    onRestore={handleRestoreCategory}
                    onDeletePermanently={handleDeletePermanently}
                  />
                ))
              )}
            </div>
          </div>

          {/* Edit Dialog */}
          <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) { setSelectedCategory(null); resetForm(); } }}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Επεξεργασία Κατηγορίας</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdateCategory} className="space-y-4">
                <div className="space-y-2">
                  <Label>Key</Label>
                  <Input
                    value={formData.key}
                    disabled
                    className="bg-slate-50"
                  />
                  <p className="text-xs text-slate-500">Το key δεν μπορεί να αλλάξει</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-name">Όνομα Κατηγορίας</Label>
                  <Input
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <DocumentFormFields 
                  documents={formData.documents}
                  onAddDocument={addDocumentField}
                  onRemoveDocument={removeDocumentField}
                  onUpdateDocument={updateDocument}
                />

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditDialogOpen(false)}
                    className="flex-1"
                  >
                    Ακύρωση
                  </Button>
                  <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800" disabled={loading}>
                    {loading ? 'Αποθήκευση...' : 'Αποθήκευση'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default ExpenseCategoryAdmin;
